import Spacings from '@dynatrace/strato-design-tokens/spacings';

/**
 * Defines the available gapSizes and their mapping to the tokens.
 * @public
 */
export const spacingProperties = {
  0: Spacings.Size0,
  2: Spacings.Size2,
  4: Spacings.Size4,
  6: Spacings.Size6,
  8: Spacings.Size8,
  12: Spacings.Size12,
  16: Spacings.Size16,
  20: Spacings.Size20,
  24: Spacings.Size24,
  32: Spacings.Size32,
  40: Spacings.Size40,
  48: Spacings.Size48,
  56: Spacings.Size56,
  64: Spacings.Size64,
};

/**
 * Union type of the available SpacingProperties.
 * @public
 */
export type SpacingProperties = keyof typeof spacingProperties;

/**
 * Order of the available gap sizes from smallest to largest.
 * @internal
 */
export const spacingPropertiesOrder: SpacingProperties[] = [
  0, 2, 4, 6, 8, 12, 16, 20, 24, 32, 40, 48, 56, 64,
];

/**
 * spacing properties with auto margin.
 * @public
 */
export const marginProperties = {
  ...spacingProperties,
  auto: 'auto',
};

/**
 * Union type of the available margins.
 * @public
 */
export type MarginProperties = keyof typeof marginProperties;

/**
 * Defines the available self positions.
 * @public
 */
const selfPosition = {
  center: 'center',
  end: 'end',
  'flex-end': 'flex-end',
  'flex-start': 'flex-start',
  'self-end': 'self-end',
  'self-start': 'self-start',
  start: 'start',
};

/**
 * Defines the available content distribution.
 * @public
 */
const contentDistribution = {
  'space-around': 'space-around',
  'space-between': 'space-between',
  'space-evenly': 'space-evenly',
  stretch: 'stretch',
};

/**
 * Defines the available content positions.
 * @public
 */
const contentPosition = {
  center: 'center',
  end: 'end',
  'flex-end': 'flex-end',
  'flex-start': 'flex-start',
  start: 'start',
};

/**
 * Defines the available justify self positions.
 * @public
 */
export const justifySelfPositionProps = {
  ...selfPosition,
  auto: 'auto',
  baseline: 'baseline',
  left: 'left',
  normal: 'normal',
  right: 'right',
  stretch: 'stretch',
};

/**
 * Union type of the available justify self positions.
 * @public
 */
export type JustifySelfPositionProps = keyof typeof justifySelfPositionProps;

/**
 * Defines the available align self positions.
 * @public
 */
export const alignSelfPositionProps = {
  ...selfPosition,
  auto: 'auto',
  baseline: 'baseline',
  normal: 'normal',
  stretch: 'stretch',
};

/**
 * Union type of the available align self positions.
 * @public
 */
export type AlignSelfPositionProps = keyof typeof alignSelfPositionProps;

/**
 * Defines the available place self positions.
 * @public
 */
export const placeSelfPositionProps = {
  ...selfPosition,
  auto: 'auto',
  baseline: 'baseline',
  normal: 'normal',
  stretch: 'stretch',
};

/**
 * Union type of the available place self positions.
 * @public
 */
export type PlaceSelfPositionProps = keyof typeof placeSelfPositionProps;

/**
 * Defines the available justify items positions.
 * @public
 */
export const justifyItemsPositionProps = {
  ...selfPosition,
  baseline: 'baseline',
  left: 'left',
  legacy: 'legacy',
  normal: 'normal',
  right: 'right',
  stretch: 'stretch',
};

/**
 * Union type of the available justify items positions.
 * @public
 */
export type JustifyItemsPositionProps = keyof typeof justifyItemsPositionProps;

/**
 * Defines the available align items positions.
 * @public
 */
export const alignItemsPositionProps = {
  ...selfPosition,
  baseline: 'baseline',
  normal: 'normal',
  stretch: 'stretch',
};

/**
 * Union type of the available align items positions
 * @public
 */
export type AlignItemsPositionProps = keyof typeof alignItemsPositionProps;

/**
 * Defines the available place items positions
 * @public
 */
export const placeItemsPositionProps = {
  ...selfPosition,
  baseline: 'baseline',
  normal: 'normal',
  stretch: 'stretch',
};

/**
 * Union type of the available place items positions
 * @public
 */
export type PlaceItemsPositionProps = keyof typeof placeItemsPositionProps;

/**
 * Defines the available justify content positions
 * @public
 */
export const justifyContentPositionProps = {
  ...contentDistribution,
  ...contentPosition,
  left: 'left',
  normal: 'normal',
  right: 'right',
};

/**
 * Union type of the available justify content positions
 * @public
 */
export type JustifyContentPositionProps =
  keyof typeof justifyContentPositionProps;

/**
 * Defines the available align content positions
 * @public
 */
export const alignContentPositionProps = {
  ...contentDistribution,
  ...contentPosition,
  baseline: 'baseline',
  normal: 'normal',
};

/**
 * Union type of the available align content positions
 * @public
 */
export type AlignContentPositionProps = keyof typeof alignContentPositionProps;

/**
 * Defines the available place content positions
 * @public
 */
export const placeContentPositionProps = {
  ...contentDistribution,
  ...contentPosition,
  baseline: 'baseline',
  normal: 'normal',
};

/**
 * Union type of the available place content positions
 * @public
 */
export type PlaceContentPositionProps = keyof typeof placeContentPositionProps;
